import React, { useState } from "react";
import { CalendarDays, Loader2, Ticket } from "lucide-react";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";

// Shown only for an event with 2-5 dates — the API sends `event_occurrences` empty otherwise, so
// a single-date event never sees a question whose only answer is "the event", and a long run of
// dates (one show plays 139 nights on one link) never gets a picker nobody could keep straight.
//
// The choice is real, not bookkeeping: a multi-date event is routinely sold one page per night,
// and the consumer shows a date's own links when it has them (`getOccurrenceTicketRefs`). An
// event-level URL claims to sell every date, which for `El Consorcio at Teatro Nuevo Alcala` —
// two nights, two different provider pages — is wrong for one of them whichever link is chosen.

const EVENT_TARGET = "__event__";

/** "Sun 30 Nov · 20:00" — the time is load-bearing: a day can run a matinee and an evening, and
 * they are separate targets with separate links. */
export function occurrenceLabel(occurrence) {
  const { local_date: date, local_time: time, name } = occurrence || {};
  if (!date) return name || "Untitled date";
  const parsed = new Date(`${date}T${time || "00:00:00"}`);
  const formatted = Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const clock = time ? time.slice(0, 5) : null;
  return [name, formatted, clock].filter(Boolean).join(" · ");
}

/**
 * Where one ticket URL should go: the event as a whole, or one of its showtimes.
 *
 * `preselectDate` is the candidate's own `date` — which night the agent says that page sells. It
 * is only ever a default: the agent reads it off the page and can be wrong about it, and the
 * admin is looking at the same link. A date it names that the event does not have was already
 * dropped server-side (`build_ticket_candidates`), so a stale preselect cannot survive to here.
 *
 * **A date with two showtimes preselects neither.** The agent answers with a date and a page
 * rarely says which performance it sells, so on a day running a matinee and an evening (42 events
 * in the index) picking the first would file the evening's link against the matinee by default —
 * and a default is exactly what nobody re-reads. Both rows are still marked, so the admin sees
 * which day the agent meant and chooses the showtime themselves.
 */
const UrlTargetModal = ({ open, onOpenChange, url, occurrences = [], preselectDate = null, busy, onConfirm }) => {
  const onDate = preselectDate
    ? occurrences.filter((occurrence) => occurrence.local_date === preselectDate)
    : [];
  const preselected = onDate.length === 1 ? onDate[0].id : null;
  const [target, setTarget] = useState(preselected || EVENT_TARGET);

  return (
    <Modal open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <ModalContent
        className="max-w-lg"
        closeDisabled={busy}
        title="Where does this link belong?"
        description="This event plays more than one date, and each may be sold separately."
      >
        <p className="mb-3 break-all rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{url}</p>
        <fieldset className="space-y-2" disabled={busy}>
          <legend className="sr-only">Target for this ticket URL</legend>
          <label
            className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-accent"
            htmlFor="url-target-event"
          >
            <input
              id="url-target-event"
              type="radio"
              name="url-target"
              className="mt-1"
              checked={target === EVENT_TARGET}
              onChange={() => setTarget(EVENT_TARGET)}
            />
            <span className="min-w-0">
              <span className="flex items-center gap-2 font-medium">
                <Ticket className="h-4 w-4" /> The event
              </span>
              {/* Said plainly because it is the part that is easy to get wrong: an event-level
                  link is not "the default", it is a claim that this page sells every date. */}
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Every date. Right for a venue page listing them all, or a provider page with a date
                selector.
              </span>
            </span>
          </label>

          {occurrences.map((occurrence) => {
            // Already there: adding it again is a no-op the server reports as success, which
            // reads as "done" for a link that did not move.
            const alreadyHere = (occurrence.urls || []).includes(url);
            return (
              <label
                key={occurrence.id}
                className={`flex items-start gap-3 rounded-lg border p-3 ${
                  alreadyHere ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-accent"
                }`}
                htmlFor={`url-target-${occurrence.id}`}
              >
                <input
                  id={`url-target-${occurrence.id}`}
                  type="radio"
                  name="url-target"
                  className="mt-1"
                  disabled={alreadyHere}
                  checked={target === occurrence.id}
                  onChange={() => setTarget(occurrence.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 font-medium">
                    <CalendarDays className="h-4 w-4" />
                    {occurrenceLabel(occurrence)}
                    {occurrence.local_date === preselectDate && (
                      <Badge variant="secondary">
                        {onDate.length > 1 ? "agent says this day — which showtime?" : "agent says this date"}
                      </Badge>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {alreadyHere
                      ? "This link is already on this showtime"
                      : occurrence.urls?.length
                        ? `${occurrence.urls.length} link${occurrence.urls.length === 1 ? "" : "s"} on this showtime`
                        : "No link on this showtime yet"}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <ModalFooter className="mt-4 px-0 pb-0">
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={() => onConfirm(target === EVENT_TARGET ? null : target)}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add link
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UrlTargetModal;
